if (!customElements.get('media-gallery')) {
  class MediaGallery extends HTMLElement {
    constructor() {
      super();

      if (Shopify.designMode) {
        setTimeout(() => this.init(), 200);
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('on:debounced-resize', this.resizeHandler);

      if (this.resizeInitHandler) {
        window.removeEventListener('on:debounced-resize', this.resizeInitHandler);
      }

      if (this.zoomInitHandler) {
        window.removeEventListener('on:debounced-resize', this.zoomInitHandler);
      }
    }

    init() {
      this.section = this.closest('.js-product');
      this.noSelectedVariant = this.hasAttribute('data-no-selected-variant');
      this.mediaGroupingEnabled = this.hasAttribute('data-media-grouping-enabled')
        && this.getMediaGroupData();
      this.stackedScroll = this.dataset.stackedScroll;
      this.stackedUnderline = this.dataset.stackedUnderline === 'true' && !this.mediaGroupingEnabled;
      this.isFeatured = this.dataset.isFeatured === 'true';
      this.viewer = this.querySelector('.media-viewer');
      this.thumbs = this.querySelector('.media-thumbs');
      this.thumbsItems = this.querySelectorAll('.media-thumbs__item');
      this.controls = this.querySelector('.media-ctrl');
      this.prevBtn = this.querySelector('.media-ctrl__btn[name="prev"]');
      this.nextBtn = this.querySelector('.media-ctrl__btn[name="next"]');
      this.counterCurrent = this.querySelector('.media-ctrl__current-item');
      this.counterTotal = this.querySelector('.media-ctrl__total-items');
      this.liveRegion = this.querySelector('.media-gallery__status');
      this.zoomLinks = this.querySelectorAll('.js-zoom-link');
      this.loadingSpinner = this.querySelector('.loading-spinner');
      this.xrButton = this.querySelector('.media-xr-button');

      if (this.hasAttribute('data-zoom-enabled')) {
        this.galleryModal = this.querySelector('.js-media-zoom-template').content.firstElementChild.cloneNode(true);
      }

      if (this.mediaGroupingEnabled && !this.noSelectedVariant) {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
      }

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        this.resizeInitHandler = this.resizeInitHandler || this.initGallery.bind(this);
        window.addEventListener('on:debounced-resize', this.resizeInitHandler);
        this.setVisibleItems();
        this.previousMediaItem = this.querySelector('.media-viewer__item.is-current-variant');
        setTimeout(() => this.customSetActiveMedia(this.previousMediaItem, this.stackedScroll === 'always'), 200);
      } else {
        this.initGallery();
      }

      if (this.zoomLinks) {
        if (this.dataset.zoomTrigger === 'hover') {
          this.triggerZoomInit();
        } else {
          this.zoomLinks.forEach((zoomLink) => {
            zoomLink.addEventListener('click', (evt) => {
              this.triggerZoomInit();
              evt.preventDefault();
            });
          });
        }
      }

      this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));

      this.querySelectorAll('[data-aux-image="true"]').forEach(item => {
        item.style.display = 'none';
      });
    }

    triggerZoomInit() {
      this.zoomInitHandler = this.zoomInitHandler || this.initZoom.bind(this);
      this.zoomEventListener = this.zoomEventListener || this.handleZoomMouseMove.bind(this);
      window.addEventListener('on:debounced-resize', this.zoomInitHandler);
      this.initZoom();
      this.zoomLinks.forEach((zoomLink) => {
        zoomLink.addEventListener('click', (evt) => {
          evt.preventDefault();
        });
      });
    }

    // Anchors extraction to "{model_ref}-{color_code}" — e.g. 800027-104, 800021-CBP, 115AT0-101, 116BC0-102
    // Model ref can be purely numeric (800027) or alphanumeric starting with a digit (115AT0, 116BC0).
    extractColorCode(imageName) {
      const matches = imageName.match(/\d[A-Z0-9]{3,}-([A-Z]{2,}|\d+)/);
      return matches ? matches[1] : null;
    }

    onVariantChange(evt) {
      if (!evt.detail.variant) return;

      if (this.mediaGroupingEnabled) {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
      }

      const colorCode = this.getColorCodeFromVariant(evt.detail.variant);

      if (colorCode) {
        this.setActiveMediaGroup(colorCode);

        const mediaWithColor = Array.from(this.viewer.querySelectorAll('[data-image-name]'))
          .filter(el => this.extractColorCode(el.dataset.imageName) === colorCode);

        if (mediaWithColor.length > 0) {
          this.customSetActiveMedia(mediaWithColor[0], true);
          return;
        }
      }

      if (evt.detail.variant.featured_media) {
        const variantMedia = this.viewer.querySelector(
          `[data-media-id="${evt.detail.variant.featured_media.id}"]`
        );

        if (variantMedia) {
          this.customSetActiveMedia(variantMedia, true);
        }
      }
    }

    // Extracts a color code using 3 strategies in order of reliability:
    // 1. Variant title prefix: "104 - MARINO / 36" or "CBP - COLOR BAJO PEDIDO / TBP"
    // 2. Featured image filename: "800027-104-36.jpg" or "800021-CBP-TBP.jpg"
    // 3. Fallback: first numeric sequence found in the title
    getColorCodeFromVariant(variant) {
      if (variant.title) {
        const titleMatch = variant.title.match(/^([A-Z]{2,}|\d+)\s*-/);
        if (titleMatch) return titleMatch[1];
      }

      if (variant.featured_media?.preview_image?.src) {
        const fileName = variant.featured_media.preview_image.src.split('/').pop().split('?')[0];
        const fileMatch = fileName.match(/\d[A-Z0-9]{3,}-([A-Z]{2,}|\d+)/);
        if (fileMatch) return fileMatch[1];
      }

      if (variant.title) {
        const numericMatch = variant.title.match(/\b(\d+)\b/);
        if (numericMatch) return numericMatch[1];
      }

      return null;
    }

    // Reads the selected option value and extracts the color code. Three formats are handled:
    // 1. Prefix: "142-ARENA" → "142"
    // 2. Slash-separated: "Color / 142 / Talla" → "142"
    // 3. Any numeric sequence in the value
    // Falls back to the raw value to preserve compatibility with string-keyed native groups.
    getMediaGroupFromOptionSelectors() {
      const optionSelectors = this.section.querySelectorAll('.option-selector');

      if (!optionSelectors.length || typeof this.getMediaGroupData().groupOptionIndex === 'undefined') {
        return null;
      }

      const selector = optionSelectors[this.getMediaGroupData().groupOptionIndex];
      if (!selector) return null;

      let selectedValue = '';
      if (selector.dataset.selectorType === 'dropdown') {
        selectedValue = selector.querySelector('.custom-select__btn').textContent.trim();
      } else {
        const checkedInput = selector.querySelector('input:checked');
        selectedValue = checkedInput ? checkedInput.value : '';
      }

      if (!selectedValue) return null;

      const prefixMatch = selectedValue.match(/^(\d+)-/);
      if (prefixMatch) return prefixMatch[1];

      for (const part of selectedValue.split('/')) {
        const trimmedPart = part.trim();
        if (/^\d+$/.test(trimmedPart)) return trimmedPart;
      }

      const numericMatch = selectedValue.match(/\b(\d+)\b/);
      if (numericMatch) return numericMatch[1];

      return selectedValue;
    }

    /**
     * Gets the variant media associations for a product.
     * @returns {?object}
     */
    getMediaGroupData() {
      if (typeof this.variantMediaData === 'undefined') {
        const dataEl = this.querySelector('.js-data-variant-media');
        this.variantMediaData = dataEl ? JSON.parse(dataEl.textContent) : false;
      }

      return this.variantMediaData;
    }

    /**
     * Builds a map of color-code groups to their viewer/thumb items.
     * Groups by color code extracted from data-image-name; product-video items are excluded.
     * @returns {object}
     */
    getMediaGroupMap() {
      if (!this.mediaGroupMap) {
        this.mediaGroupMap = { groups: {} };

        this.viewerItems = this.querySelectorAll('.media-viewer__item');

        this.viewerItems.forEach((item) => {
          if (!item.dataset.imageName) return;
          if (item.dataset.mediaId === 'product-video') return;

          const colorCode = this.extractColorCode(item.dataset.imageName);
          if (!colorCode) return;

          if (!this.mediaGroupMap.groups[colorCode]) {
            this.mediaGroupMap.groups[colorCode] = { name: colorCode, items: [] };
          }

          const groupItem = { main: item };

          if (this.thumbs) {
            groupItem.thumb = this.thumbs.querySelector(
              `[data-image-name="${item.dataset.imageName}"].media-thumbs__item`
            );
          }

          this.mediaGroupMap.groups[colorCode].items.push(groupItem);
        });

        this.mediaGroupMap.groupFromItem = (item) => {
          if (!item || !item.dataset.imageName) return null;

          const itemColorCode = this.extractColorCode(item.dataset.imageName);

          if (!itemColorCode || !this.mediaGroupMap.groups[itemColorCode]) return null;

          return this.mediaGroupMap.groups[itemColorCode];
        };
      }

      return this.mediaGroupMap;
    }

    /**
     * Shows only images belonging to the active color group.
     * product-video items are always kept visible regardless of group.
     * @param {string} [groupName] - Group key to show; falls back to current item's group if omitted.
     */
    setActiveMediaGroup(groupName) {
      const mediaGroupMap = this.getMediaGroupMap();
      let selectedGroup = groupName
        ? mediaGroupMap.groups[groupName]
        : mediaGroupMap.groupFromItem(this.currentItem);

      // Full option display names like "CBP - COLOR BAJO PEDIDO" won't match the short key "CBP" — try extracting the prefix.
      if (!selectedGroup && typeof groupName === 'string') {
        const shortCode = groupName.match(/^([A-Z]{2,}|\d+)/)?.[1];
        if (shortCode && shortCode !== groupName) {
          selectedGroup = mediaGroupMap.groups[shortCode];
        }
      }

      if (selectedGroup) {
        if (this.currentGroup !== selectedGroup) {
          this.currentGroup = selectedGroup;

          this.viewerItems.forEach((item) => {
            if (item.dataset.mediaType === 'video' && item.dataset.mediaId === 'product-video') return;
            item.style.display = 'none';
            item.classList.remove('media-viewer__item--single');
          });

          this.thumbsItems.forEach((item) => {
            if (item.dataset.mediaId === 'product-video') return;
            item.style.display = 'none';
          });

          let currentItemIsVisible = false;
          selectedGroup.items.forEach((item) => {
            item.main.style.display = '';
            if (item.thumb) {
              item.thumb.style.display = '';
            }
            if (item.main === this.currentItem) {
              currentItemIsVisible = true;
            }
          });
          this.setVisibleItems();

          // If current item is not in this group, set it as the active item
          if (!currentItemIsVisible) {
            this.customSetActiveMedia(selectedGroup.items[0].main, true);
          }

          // Handle single images on stacked view
          if (selectedGroup.items.length === 1) {
            selectedGroup.items[0].main.classList.add('media-viewer__item--single');
          }
        }
      } else {
        this.viewerItems.forEach((item) => {
          item.style.display = '';
        });
        this.thumbsItems.forEach((item) => {
          item.style.display = '';
        });
      }
    }

    /**
     * Initialises the media gallery slider and associated controls.
     */
    initGallery() {
      this.setVisibleItems();
      if (this.visibleItems.length <= 1) return;

      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
      this.currentIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);
      this.currentItem = this.visibleItems[this.currentIndex];
      this.addListeners();

      if (this.thumbs && this.currentItem) {
        this.currentThumb = this.thumbs.querySelector(
          `[data-media-id="${this.currentItem.dataset.mediaId}"]`
        );
      }

      if (!this.isFeatured && document.hasFocus()) {
        // Eager load the slider images for smooth UX
        this.viewer.querySelectorAll('.product-image[loading="lazy"]').forEach((img, index) => {
          setTimeout(() => {
            img.loading = 'eager';
          }, 500 * (index + 1));
        });
      }

      const currentItem = this.querySelector('.media-viewer__item.is-current-variant');
      this.customSetActiveMedia(currentItem, true);
    }

    addListeners() {
      this.viewer.addEventListener('scroll', this.handleScroll.bind(this));
      if (this.controls) this.controls.addEventListener('click', this.handleNavClick.bind(this));
      if (this.thumbs) this.thumbs.addEventListener('click', this.handleThumbClick.bind(this));
      this.resizeHandler = this.resizeHandler || this.handleResize.bind(this);
      window.addEventListener('on:debounced-resize', this.resizeHandler);
    }

    /**
     * Initialises zoom on hover for desktop viewports.
     */
    initZoom() {
      this.zoomLinks.forEach((el) => {
        const zoomWidth = Number(el.querySelector('.zoom-image').dataset.originalWidth || 0);
        const imageWidth = el.querySelector('.product-image').getBoundingClientRect().width;
        if (theme.mediaMatches.md && ((zoomWidth - 75) > (imageWidth))) {
          el.addEventListener('mousemove', this.zoomEventListener);
          el.classList.remove('pointer-events-none');
        } else {
          el.removeEventListener('mousemove', this.zoomEventListener);
          el.classList.add('pointer-events-none');
        }
      });
    }

    /**
     * Handles mousemove on a zoomable image — pans the high-res image under the cursor.
     * @param {MouseEvent} evt
     */
    handleZoomMouseMove(evt) {
      if (this.hasAttribute('data-zoom-enabled') && !this.dataset.zoomTrigger && !evt.target.closest('.gallery-zoom-modal')) {
        return;
      }

      const hoverElem = evt.currentTarget;
      const zoomImage = hoverElem.querySelector('.js-zoom-image');

      // Download the zoom image if necessary
      if (zoomImage.dataset.src) {
        this.loadingSpinner.classList.remove('loading-spinner--out');

        const img = new Image();
        img.src = zoomImage.dataset.src;
        img.onload = () => {
          zoomImage.src = img.src;
          hoverElem.classList.remove('media--zoom-not-loaded');
          this.loadingSpinner.classList.add('loading-spinner--out');
        };
        zoomImage.removeAttribute('data-src');
      }

      try {
        const offsetX = evt.offsetX ? evt.offsetX : evt.touches[0].pageX;
        const offsetY = evt.offsetY ? evt.offsetY : evt.touches[0].pageY;
        const x = (offsetX / zoomImage.offsetWidth) * 100;
        const y = (offsetY / zoomImage.offsetHeight) * 100;
        zoomImage.style.objectPosition = `${x}% ${y}%`;
      } catch (err) {
        // Fail silently
      }
    }

    /**
     * Handles 'scroll' events on the main media container.
     */
    handleScroll() {
      const newIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);

      if (newIndex !== this.currentIndex) {
        const viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

        // If scroll wasn't caused by a resize event, update the active media.
        if (viewerItemOffset === this.viewerItemOffset) {
          this.customSetActiveMedia(this.visibleItems[newIndex], false);
        }
      }
    }

    /**
     * Handles 'click' events on the controls container.
     * @param {object} evt - Event object.
     */
    handleNavClick(evt) {
      if (!evt.target.matches('.media-ctrl__btn')) return;

      const itemToShow = evt.target === this.nextBtn
        ? this.visibleItems[this.currentIndex + 1]
        : this.visibleItems[this.currentIndex - 1];

      this.viewer.scrollTo({ left: itemToShow.offsetLeft, behavior: 'smooth' });
    }

    handleThumbClick(evt) {
      const thumb = evt.target.closest('[data-image-name]');
      if (!thumb) return;

      // product-video uses a special media-id and has no matching viewer item by image-name
      if (thumb.dataset.mediaId === 'product-video') {
        const videoItem = this.querySelector('[data-media-id="product-video"]');
        if (videoItem) {
          this.customSetActiveMedia(videoItem);
          const videoElement = videoItem.querySelector('video');
          if (videoElement) {
            videoElement.currentTime = 0;
            videoElement.play().catch(() => {});
          }
          return;
        }
      }

      let itemToShow = this.querySelector(`[data-image-name="${thumb.dataset.imageName}"]`);

      if (!itemToShow && thumb.dataset.mediaId) {
        itemToShow = this.querySelector(`[data-media-id="${thumb.dataset.mediaId}"]`);
      }

      if (!itemToShow) return;

      this.customSetActiveMedia(itemToShow);
      MediaGallery.playActiveMedia(itemToShow);
    }

    /**
     * Handles debounced 'resize' events on the window.
     */
    handleResize() {
      // Reset distance from leading edge of one slide to the next.
      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

      if (this.thumbs && this.currentThumb) {
        this.checkThumbVisibilty(this.currentThumb);
      }
    }

    /**
     * Stub for variant-picker calls. Listening to change event instead.
     */
    // eslint-disable-next-line class-methods-use-this
    setActiveMedia() {}

    /**
     * Sets the active media item.
     * @param {Element} mediaItem - Media element to set as active.
     * @param {boolean} [scrollToItem=true] - Scroll to the active media item.
     */
    customSetActiveMedia(mediaItem, scrollToItem = true) {
      if (mediaItem === this.currentItem) return;
      window.pauseAllMedia(this);

      // pauseAllMedia doesn't reach the product-video element — pause it explicitly
      const productVideo = this.querySelector('[data-media-id="product-video"] video');
      if (productVideo) productVideo.pause();

      if (mediaItem.dataset.mediaId === 'product-video') {
        mediaItem.querySelector('video')?.play().catch(() => {});
      }

      this.currentItem = mediaItem;
      this.currentIndex = this.visibleItems.indexOf(this.currentItem);

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        // Update the active class and scroll to the active media
        if (this.stackedUnderline) {
          if (this.previousMediaItem) this.previousMediaItem.classList.remove('is-active');
          mediaItem.classList.add('is-active');
          this.previousMediaItem = mediaItem;
        }

        if (this.stackedScroll !== 'never') {
          const y = mediaItem.getBoundingClientRect().top
            + document.documentElement.scrollTop - 150;

          // If the element is far enough away to scroll to it
          if (Math.abs(y - document.documentElement.scrollTop) > 300) {
            window.scrollTo({
              top: y < 100 ? 0 : y,
              behavior: 'smooth'
            });
          }
        }
        return;
      }

      if (scrollToItem) this.viewer.scrollTo({ left: this.currentItem.offsetLeft });
      if (this.thumbs) this.setActiveThumb();

      if (this.controls) {
        if (this.prevBtn) {
          this.prevBtn.disabled = this.currentIndex === 0;
        }

        if (this.nextBtn) {
          this.nextBtn.disabled = this.currentIndex === this.visibleItems.length - 1;
        }

        if (this.counterCurrent) {
          this.counterCurrent.textContent = this.currentIndex + 1;
        }
      }

      this.announceLiveRegion(this.currentItem, this.currentIndex + 1);

      if (this.xrButton && mediaItem.dataset.mediaType === 'model') {
        this.xrButton.dataset.shopifyModel3dId = mediaItem.dataset.mediaId;
      }
    }

    /**
     * Sets the active thumbnail.
     */
    setActiveThumb() {
      this.currentThumb = this.thumbs.querySelector(
        `[data-media-id="${this.currentItem.dataset.mediaId}"]`
      );
      const btn = this.currentThumb.querySelector('button');

      this.thumbs.querySelectorAll('.media-thumbs__btn').forEach((el) => {
        el.classList.remove('is-active');
        el.removeAttribute('aria-current');
      });

      btn.classList.add('is-active');
      btn.setAttribute('aria-current', 'true');
      this.checkThumbVisibilty(this.currentThumb);
    }

    /**
     * Creates an array of the visible media items.
     */
    setVisibleItems() {
      this.viewerItems = this.querySelectorAll('.media-viewer__item');
      this.visibleItems = Array.from(this.viewerItems).filter((el) => el.clientWidth > 0);
      if (this.counterTotal) {
        this.counterTotal.textContent = this.visibleItems.length;
      }
    }

    /**
     * Ensures a thumbnail is in the visible area of the slider.
     * @param {Element} thumb - Thumb item element.
     */
    checkThumbVisibilty(thumb) {
      const scrollPos = this.thumbs.scrollLeft;
      const lastVisibleThumbOffset = this.thumbs.clientWidth + scrollPos;
      const thumbOffset = thumb.offsetLeft;

      if (thumbOffset + thumb.clientWidth > lastVisibleThumbOffset || thumbOffset < scrollPos) {
        this.thumbs.scrollTo({ left: thumbOffset, behavior: 'smooth' });
      }
    }

    /**
     * Updates the media gallery status.
     * @param {Element} mediaItem - Active media element.
     * @param {number} index - Active media index.
     */
    announceLiveRegion(mediaItem, index) {
      const image = mediaItem.querySelector('.media-viewer img');
      if (!image) return;

      this.liveRegion.setAttribute('aria-hidden', 'false');
      this.liveRegion.innerHTML = theme.strings.imageAvailable.replace('[index]', index);

      setTimeout(() => {
        this.liveRegion.setAttribute('aria-hidden', 'true');
      }, 2000);
    }

    /**
     * Loads the deferred media for the active item.
     * @param {Element} mediaItem - Active media element.
     */
    static playActiveMedia(mediaItem) {
      window.pauseAllMedia();
      const deferredMedia = mediaItem.querySelector('deferred-media');
      if (deferredMedia) deferredMedia.loadContent();
    }
  }

  customElements.define('media-gallery', MediaGallery);
}
